"use client";

import { useEffect, useRef } from "react";
import { useInView } from "framer-motion";
import { getDevicePerformance, createFpsThrottler } from "../utils/performance";

const TEXT = "SOLARCH";
const CANVAS_WIDTH = 1024;
const CANVAS_HEIGHT = 300;

const VERTEX_SHADER = `#version 300 es
precision highp float;

in vec2 aHome;

uniform vec2 uResolution;
uniform float uTime;
uniform float uProgress;
uniform float uScale;
uniform vec2 uTextCenter;
uniform float uDpr;

out float vLife;
out float vBurnState;
out float vRandom;

float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

void main() {
    float h1 = hash(aHome);
    float h2 = hash(aHome + vec2(13.5));
    float h3 = hash(aHome + vec2(42.1));
    
    vRandom = h1;
    
    vec2 gridCell = floor(aHome / 20.0);
    float spotNoise = hash(gridCell);
    
    // Quicker random disintegration
    float startBurn = spotNoise * 0.8 + h1 * 0.5;
    
    // Shorter burn duration
    float burnDuration = 1.0 + h2 * 0.5; 
    
    float timeSinceIgnition = max(0.0, uProgress - startBurn);
    
    vBurnState = clamp(timeSinceIgnition / burnDuration, 0.0, 1.0);
    vLife = 1.0 - vBurnState;
    
    vec2 pos = (aHome - uTextCenter) * uScale + (uResolution / 2.0);
    
    if (timeSinceIgnition > 0.0) {
        float activeTime = timeSinceIgnition; 
        float upSpeed = mix(30.0, 70.0, h2) * uScale;
        pos.y -= (upSpeed * activeTime) + (activeTime * activeTime * 30.0 * uScale);
        float sway = sin(uTime * 3.0 + h3 * 10.0) * mix(10.0, 40.0, h1) * uScale;
        pos.x += sway * activeTime;
    }
    
    vec2 clipPos = (pos / uResolution) * 2.0 - 1.0;
    clipPos.y *= -1.0;
    
    gl_Position = vec4(clipPos, 0.0, 1.0);
    
    float baseSize = mix(2.0, 4.5, h2);
    
    if (vBurnState == 0.0) {
        gl_PointSize = baseSize * uScale * uDpr;
    } else {
        gl_PointSize = baseSize * mix(0.1, 1.2, vLife) * uScale * uDpr; 
    }
    
    gl_PointSize = max(gl_PointSize, 0.0); 
}
`;

const FRAGMENT_SHADER = `#version 300 es
precision highp float;

in float vLife;
in float vBurnState;
in float vRandom;

out vec4 outColor;

void main() {
    vec2 pc = gl_PointCoord - 0.5;
    float dist = length(pc);
    if (dist > 0.5) discard;
    
    float alpha = smoothstep(0.5, 0.1, dist);
    
    if (vBurnState == 0.0) {
        float glow = mix(0.9, 1.0, vRandom);
        outColor = vec4(0.95 * glow, 0.9 * glow, 0.85 * glow, alpha);
    } else {
        vec3 core = vec3(1.0, 0.9, 0.7);
        vec3 yellow = vec3(1.0, 0.8, 0.2);
        vec3 orange = vec3(1.0, 0.4, 0.0);
        vec3 red = vec3(0.6, 0.1, 0.0);
        
        vec3 color;
        if (vLife > 0.8) color = mix(yellow, core, (vLife - 0.8) / 0.2);
        else if (vLife > 0.4) color = mix(orange, yellow, (vLife - 0.4) / 0.4);
        else color = mix(red, orange, vLife / 0.4);
        
        float lifeAlpha = smoothstep(0.0, 0.4, vLife);
        
        outColor = vec4(color, alpha * lifeAlpha);
    }
}
`;

function createShader(gl: WebGL2RenderingContext, type: number, source: string) {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.error("Shader compile error:", gl.getShaderInfoLog(shader));
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

function generateTextParticles(text: string, targetParticles: number) {
  const canvas = document.createElement("canvas");
  canvas.width = CANVAS_WIDTH;
  canvas.height = CANVAS_HEIGHT;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return new Float32Array();
  
  ctx.fillStyle = "black";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  
  ctx.font = '700 96px "Outfit", sans-serif';
  (ctx as any).letterSpacing = "0.2em";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "white";
  ctx.fillText(text, canvas.width / 2, canvas.height / 2);
  
  const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
  const positions: number[] = [];
  
  // Deterministic approach: instead of random attempts, sample in a grid
  // to avoid expensive rejection loops
  const step = Math.max(1, Math.floor(Math.sqrt((canvas.width * canvas.height) / (targetParticles * 3))));
  for (let y = 0; y < canvas.height; y += step) {
    for (let x = 0; x < canvas.width; x += step) {
      const index = (y * canvas.width + x) * 4;
      if (imgData[index] > 128) {
        positions.push(x + Math.random() - 0.5, y + Math.random() - 0.5);
        if (positions.length >= targetParticles * 2) break;
      }
    }
    if (positions.length >= targetParticles * 2) break;
  }
  
  return new Float32Array(positions);
}

export default function FireText() {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isBurningRef = useRef(false);
  const isInView = useInView(containerRef, { once: false, amount: 0.1 });
  
  const isInViewRef = useRef(isInView);
  useEffect(() => {
    isInViewRef.current = isInView;
  }, [isInView]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (isInView && !isBurningRef.current) {
      timer = setTimeout(() => {
        isBurningRef.current = true;
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [isInView]);

  useEffect(() => {
    let animationFrameId: number;
    let positionBuffer: WebGLBuffer | null = null;
    let program: WebGLProgram | null = null;
    let vao: WebGLVertexArrayObject | null = null;
    let gl: WebGL2RenderingContext | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let isDestroyed = false;

    const perf = getDevicePerformance();
    const fpsThrottler = createFpsThrottler(perf.targetFps);

    document.fonts.ready.then(() => {
      if (isDestroyed) return;
      const canvas = canvasRef.current;
      if (!canvas) return;

      gl = canvas.getContext("webgl2", {
        alpha: true,
        antialias: false,
        depth: false,
      });

      if (!gl) {
        console.error("WebGL2 not supported");
        return;
      }

      gl.enable(gl.BLEND);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

      const vertexShader = createShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
      const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
      if (!vertexShader || !fragmentShader) return;
      
      program = gl.createProgram();
      if (!program) return;
      
      gl.attachShader(program, vertexShader);
      gl.attachShader(program, fragmentShader);
      gl.linkProgram(program);

      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        console.error("Program link error:", gl.getProgramInfoLog(program));
        return;
      }
      gl.useProgram(program);

      const locs = {
        position: gl.getAttribLocation(program, "aHome"),
        uResolution: gl.getUniformLocation(program, "uResolution"),
        uTime: gl.getUniformLocation(program, "uTime"),
        uProgress: gl.getUniformLocation(program, "uProgress"),
        uScale: gl.getUniformLocation(program, "uScale"),
        uTextCenter: gl.getUniformLocation(program, "uTextCenter"),
        uDpr: gl.getUniformLocation(program, "uDpr"),
      };

      const particleData = generateTextParticles(TEXT, perf.fireTextParticles);
      const particleCount = particleData.length / 2;

      vao = gl.createVertexArray();
      gl.bindVertexArray(vao);

      positionBuffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, particleData, gl.STATIC_DRAW);
      gl.enableVertexAttribArray(locs.position);
      gl.vertexAttribPointer(locs.position, 2, gl.FLOAT, false, 0, 0);

      gl.uniform2f(locs.uTextCenter, CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2);

      let startTime = performance.now();
      let currentProgress = 0;
      let lastWidth = 0;
      let lastHeight = 0;

      const handleResize = () => {
        if (!gl || isDestroyed) return;
        const dpr = Math.min(window.devicePixelRatio || 1, perf.maxDpr);
        const width = canvas.clientWidth;
        const height = canvas.clientHeight;
        
        if (width === lastWidth && height === lastHeight) return;
        lastWidth = width;
        lastHeight = height;

        canvas.width = width * dpr;
        canvas.height = height * dpr;
        
        gl.viewport(0, 0, canvas.width, canvas.height);
        if (locs.uResolution) gl.uniform2f(locs.uResolution, canvas.width, canvas.height);
        if (locs.uDpr) gl.uniform1f(locs.uDpr, dpr);
        
        const scale = 0.25 * dpr;
        if (locs.uScale) gl.uniform1f(locs.uScale, scale);
      };

      handleResize();
      
      resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(handleResize);
      });
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }

      const renderLoop = (now: number) => {
        if (isDestroyed || !gl) return;
        
        if (!isInViewRef.current || document.visibilityState === "hidden" || perf.isReducedMotion) {
          animationFrameId = requestAnimationFrame(renderLoop);
          startTime = now - (currentProgress * 1000 / 1.5);
          return;
        }

        if (!fpsThrottler.shouldRender(now)) {
          animationFrameId = requestAnimationFrame(renderLoop);
          return;
        }

        const dt = (now - startTime) / 1000;
        startTime = now;

        if (isBurningRef.current) {
          currentProgress += dt * 1.5; 
        }

        if (currentProgress >= 7.0) {
          currentProgress = 7.0;
          gl.clearColor(0.0, 0.0, 0.0, 0.0);
          gl.clear(gl.COLOR_BUFFER_BIT);
          return;
        }

        if (locs.uTime) gl.uniform1f(locs.uTime, now / 1000);
        if (locs.uProgress) gl.uniform1f(locs.uProgress, currentProgress);

        gl.clearColor(0.0, 0.0, 0.0, 0.0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.useProgram(program);
        gl.bindVertexArray(vao);
        gl.drawArrays(gl.POINTS, 0, particleCount);

        animationFrameId = requestAnimationFrame(renderLoop);
      };

      fpsThrottler.reset(performance.now());
      animationFrameId = requestAnimationFrame(renderLoop);
    });

    return () => {
      isDestroyed = true;
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
      if (resizeObserver) resizeObserver.disconnect();
      if (gl) {
        if (positionBuffer) gl.deleteBuffer(positionBuffer);
        if (vao) gl.deleteVertexArray(vao);
        if (program) gl.deleteProgram(program);
        gl.getExtension('WEBGL_lose_context')?.loseContext();
      }
    };
  }, []);

  return (
    <div ref={containerRef} className="relative w-full h-full flex flex-col items-center justify-center overflow-hidden">
      <canvas 
        ref={canvasRef} 
        className="absolute inset-0 w-full h-full"
      />
    </div>
  );
}

